import { useEffect } from "react";
import {
    Phone,
    ArrowUpRight,
    Check,
    Wind,
    Thermometer,
    DollarSign,
    ShieldCheck,
    Lightbulb,
    Flame,
    Wrench,
    Droplets,
    Home,
    Gauge,
    Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { SITE } from "@/lib/site";
import { useSeo, breadcrumbSchema, serviceSchema } from "@/lib/seo";

// Real fan / ventilation photos sourced from Wikimedia Commons (CC-licensed) and
// downloaded locally for fast serving. Attribution at bottom of page.
const IMG_HERO = "/images/fans/whole-house-fan-motion.jpg";
const IMG_WHOLE_HOUSE = "/images/fans/whole-house-fan-installed.jpg";
const IMG_ATTIC_INSIDE = "/images/fans/attic-fan-underneath-roof.jpg";
const IMG_ATTIC_CAP = "/images/fans/attic-fan-weatherproof-cap.jpg";
const IMG_BATH = "/images/fans/bathroom-ceiling-vent.jpg";

const TOP_BENEFITS = [
    { i: Thermometer, t: "Drop indoor temps 10–25°F", d: "A properly-sized whole house fan pulls in cool night air and flushes the day's heat in minutes." },
    { i: DollarSign, t: "Cut cooling bills 50–90%", d: "Whole house fans use roughly 10% of the energy a central A/C uses to keep you comfortable." },
    { i: ShieldCheck, t: "Save your roof", d: "An attic that's properly vented runs 30–40°F cooler — and your shingles, decking, and insulation last longer for it." },
    { i: Droplets, t: "Kill bathroom moisture", d: "A correctly-vented bath fan stops mold, peeling paint, and warped trim before they start." },
];

const STATS = [
    { n: "10–25°F", t: "Indoor temp drop after a 15-minute whole house fan flush on a cool Oregon evening." },
    { n: "85–90%", t: "Less electricity used vs. running your central A/C for the same comfort." },
    { n: "30–60", t: "Full air changes per hour — your whole home flushed of stale, hot air." },
    { n: "150°F+", t: "Common attic temps in summer without proper venting. Attic fans pull that down hard." },
];

const WHF_BENEFITS = [
    "Cools the whole house in 10–15 minutes, not 30+",
    "Pulls heat out of walls, floors & furniture (A/C just cools the air)",
    "Runs on a fraction of the wattage of a central A/C",
    "Insulated, R-value-rated dampers on modern units — no winter heat loss",
    "Quiet ducted models available (45–55 dB) for bedrooms",
    "Pairs with A/C: cool nights with the fan, blast A/C only when needed",
];

const ATTIC_BENEFITS = [
    "Drops peak attic temps from 150°F+ down to closer to outside",
    "Reduces cooling load on your A/C — often 10–15% less runtime",
    "Extends shingle, decking, and underlayment life",
    "Prevents winter moisture buildup, ice damming, and rot",
    "Solar-powered, hard-wired, or smart-thermostat models",
    "Works with intake vents (soffit/gable) for a balanced system",
];

const BATH_OPTIONS = [
    { i: Wind, t: "Fan-only retrofit", d: "Quiet 80–110 CFM upgrades. Replace that noisy 1990s rattler with a 0.3–1.0 sone humidity-sensing fan." },
    { i: Lightbulb, t: "Light + Fan combo", d: "One ceiling fixture, one switch (or two). Integrated LED + exhaust fan in a single low-profile housing." },
    { i: Flame, t: "Light + Heat + Fan combo", d: "Built-in infrared or ceramic heater takes the chill off after a shower. Perfect for mobile home and master baths." },
    { i: Gauge, t: "Humidity-sensing auto", d: "Senses moisture and runs the fan automatically — then shuts off. No more leaving it on all morning by accident." },
];

const UPGRADE_REASONS = [
    "It's loud, rattly, or shaking the ceiling",
    "Mirror still fogs after a long shower",
    "Bathroom ceiling, trim, or paint is peeling or staining",
    "Attic feels like a furnace mid-July",
    "A/C runs nonstop on 90°F+ Medford afternoons",
    "Power bills climb every summer",
];

export default function Fans() {
    useSeo({
        title: "Whole House Fans, Attic Fans & Bathroom Fan Installation · Medford, Oregon | 5 Star Construction",
        description: "Whole house fans, attic fans, and bathroom exhaust fan installation & retrofits in Medford, Ashland, and the Rogue Valley. Cool your home 10–25°F and cut cooling bills 50–90%. Licensed Oregon contractor (CCB #232477). Free quotes — 541-292-9620.",
        path: "/fans",
        jsonLdKey: "fans",
        jsonLd: [
            serviceSchema({
                name: "Whole House Fan, Attic Fan & Bathroom Fan Installation",
                description: "Installation, retrofit, and upgrade of whole house fans, attic fans, and bathroom exhaust fan combos (fan-only, light/fan, light/heat/fan) for homes in Medford and Southern Oregon.",
                path: "/fans",
                serviceType: "Whole House Fan Installation",
            }),
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "Services", path: "/#services" },
                { name: "Fans", path: "/fans" },
            ]),
        ],
    });
    // (Legacy useEffect removed — replaced by useSeo)
    useEffect(() => {}, []);

    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />

            {/* Hero */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-8">
                        <div className="inline-flex items-center gap-2 mb-6 bg-[#FFD400] border border-[#FFD400] px-3 py-1.5">
                            <Wind className="w-4 h-4 text-[#111111]" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase text-[#111111]">Whole-House · Attic · Bathroom Fans</span>
                        </div>
                        <h1 className="font-['Anton'] tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-7xl uppercase">
                            Move the air.
                            <br />
                            <span className="text-[#FFD400]">Drop the bill.</span>
                        </h1>
                        <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#F6F5F2]/80 max-w-2xl">
                            Whole house fans, attic fans, and bathroom exhaust fans — installed, retrofitted, and upgraded by a real Oregon contractor. Cool your house for pennies on the dollar, kill moisture before it kills your trim, and save your shingles from a 150°F attic.
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-4">
                            <a href={SITE.phoneHref} data-testid="fans-call-cta">
                                <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                                    <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                    CALL {SITE.phoneDisplay}
                                </Button>
                            </a>
                            <a href="/#contact" data-testid="fans-quote-cta">
                                <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#F6F5F2] bg-transparent text-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] font-bold tracking-wide text-base font-['Manrope']">
                                    REQUEST A FREE QUOTE
                                    <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                                </Button>
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-4 relative">
                        <div className="relative aspect-square w-full max-w-sm mx-auto lg:max-w-none overflow-hidden border-2 border-[#F6F5F2]/20 shadow-block">
                            <img
                                src={IMG_HERO}
                                alt="Whole house fan running at full speed — blades caught mid-motion in the ceiling housing"
                                loading="eager"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute bottom-0 inset-x-0 bg-[#FFD400] text-[#111111] p-4">
                                <div className="font-['Manrope'] text-[11px] uppercase tracking-[0.22em] font-bold">Whole house fan · running</div>
                                <div className="font-display text-lg font-extrabold mt-0.5">30–60 air changes per hour</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Top benefits grid */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-8 mb-12">
                        <div className="lg:col-span-5">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Why these fans pay for themselves</div>
                            <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                                Cool homes.
                                <br />
                                Dry bathrooms.
                                <br />
                                Smaller bills.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-3">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                Southern Oregon has a near-perfect climate for ventilation: hot dry afternoons and cool nights. With the right fans, you can dump the day&apos;s heat in 15 minutes flat and barely run your A/C. We size, install, and retrofit fans the right way — code-compliant, balanced with proper intake, and finished clean.
                            </p>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {TOP_BENEFITS.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-[#F6F5F2] border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm transition-all p-6">
                                <div className="w-12 h-12 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center mb-5">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats band */}
            <section className="bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-20">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#F6F5F2]/15 border border-[#F6F5F2]/15">
                        {STATS.map((s) => (
                            <div key={s.n} className="bg-[#111111] p-7" data-testid={`fans-stat-${s.n.replace(/[^0-9]/g, "")}`}>
                                <div className="font-['Anton'] text-5xl md:text-6xl text-[#FFD400] tracking-tight leading-none">{s.n}</div>
                                <div className="mt-3 font-['Manrope'] text-sm text-[#F6F5F2]/80 leading-relaxed">{s.t}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Whole House Fans deep dive */}
            <section id="whole-house" className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 relative">
                        <div className="aspect-[5/4] overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111] max-w-md lg:max-w-none mx-auto">
                            <img
                                src={IMG_WHOLE_HOUSE}
                                alt="Whole house fan installed in a hallway ceiling — shutters visible from below"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="absolute -top-4 -left-4 hidden md:block bg-[#FFD400] text-[#111111] p-4 border-2 border-[#111111] shadow-block-sm">
                            <div className="font-['Anton'] text-3xl tracking-tight leading-none">~10%</div>
                            <div className="font-['Manrope'] text-[11px] uppercase tracking-[0.18em] font-bold mt-1">A/C energy use</div>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">01 · Whole house fans</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Flip a switch, flush the house.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                            A whole house fan is a big, ceiling-mounted fan that pulls cool outside air through your open windows and pushes hot stale air out through the attic vents. On a typical Medford summer night when it drops into the 60s, you can take a 78°F house down to <strong>62°F in under 20 minutes</strong> — and the structure itself stays cool well into the next day.
                        </p>
                        <p className="mt-4 font-['Manrope'] text-base text-[#5F5F5F] leading-relaxed">
                            Even on grid-tied A/C-heavy homes, owners typically see <strong>50–90% lower cooling bills</strong> over a season once a whole house fan goes in, because the A/C compressor barely needs to kick on at all.
                        </p>
                        <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                            {WHF_BENEFITS.map((b) => (
                                <li key={b} className="flex items-start gap-2.5 font-['Manrope'] text-sm text-[#111812]">
                                    <Check className="w-4 h-4 text-[#E11D2A] shrink-0 mt-1" strokeWidth={3} />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Attic Fans deep dive */}
            <section id="attic" className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">02 · Attic fans</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Stop cooking your shingles.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                            On a 95°F Medford afternoon, your unvented attic can hit <strong>150°F or hotter</strong>. That heat radiates down through the ceiling, fights your A/C, bakes your insulation, and shortens your roof&apos;s life by years. A properly-sized attic fan — solar, hard-wired, or thermostat-driven — pulls that heat out and drops attic temps by <strong>30–40°F</strong>.
                        </p>
                        <p className="mt-4 font-['Manrope'] text-base text-[#5F5F5F] leading-relaxed">
                            Most homes see a <strong>10–15% drop in summer A/C runtime</strong> from a good attic fan alone. Pair it with a whole house fan and you&apos;re cooling for almost nothing.
                        </p>
                        <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                            {ATTIC_BENEFITS.map((b) => (
                                <li key={b} className="flex items-start gap-2.5 font-['Manrope'] text-sm text-[#111812]">
                                    <Check className="w-4 h-4 text-[#E11D2A] shrink-0 mt-1" strokeWidth={3} />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-5 order-1 lg:order-2 grid grid-cols-2 gap-4 max-w-md lg:max-w-none mx-auto">
                        <div className="aspect-square overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111]">
                            <img
                                src={IMG_ATTIC_INSIDE}
                                alt="Attic fan viewed from underneath the roof — installed between rafters with weatherproof housing above"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="aspect-square overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111] mt-6">
                            <img
                                src={IMG_ATTIC_CAP}
                                alt="Attic fan weatherproof cap installed on the roof — exhaust outlet for hot attic air"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Bathroom Exhaust Fans + Retrofits */}
            <section id="bath" className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-12 items-start">
                        <div className="lg:col-span-4">
                            <div className="aspect-[5/4] overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111] max-w-sm lg:max-w-none mx-auto">
                                <img
                                    src={IMG_BATH}
                                    alt="Bathroom ceiling with exhaust vent and light fixture — typical retrofit target"
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-8">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">03 · Bath exhaust fans &amp; retrofits</div>
                            <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                                Retire the rattler. Upgrade to a <span className="text-[#E11D2A]">light · heat · fan</span> combo.
                            </h2>
                            <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                If your bathroom fan sounds like a Cessna taking off — or it does basically nothing for the mirror fog — it&apos;s almost certainly an undersized, decades-old builder-grade unit. We retrofit those into modern, quiet, code-compliant combos. One opening in the ceiling, one ducted run to the outside, one cleaner-looking finish.
                            </p>
                        </div>
                    </div>

                    {/* Retrofit options grid */}
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {BATH_OPTIONS.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-white border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm transition-all p-6" data-testid={`bath-option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <div className="w-12 h-12 bg-[#111111] text-[#FFD400] border border-[#111111] flex items-center justify-center mb-5">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 bg-[#FFD400] border-2 border-[#111111] p-6 md:p-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
                        <div>
                            <div className="font-['Manrope'] text-xs font-bold tracking-[0.22em] uppercase text-[#111111]">Manufactured home specialty</div>
                            <p className="mt-2 font-display text-xl md:text-2xl font-extrabold text-[#111111] leading-snug">
                                Light/heat/fan combos are a game-changer for chilly mobile-home master baths — quick to retrofit, big comfort boost.
                            </p>
                        </div>
                        <a href={SITE.phoneHref} data-testid="bath-cta-call" className="shrink-0">
                            <Button className="rounded-none h-12 px-6 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide font-['Manrope']">
                                <Phone className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Upgrades to existing fans */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Already have one? Upgrade it.</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Newer motors, smarter controls, way less noise.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                            Your existing attic or whole house fan can usually be upgraded without re-cutting the opening. New EC/DC motors are dramatically quieter and pull less power. Smart thermostats and humidistats automate the on/off. We swap the motor, dampers, controls, and ducting — keep the framing, save the cost.
                        </p>
                        <div className="mt-8 flex gap-3">
                            <a href={SITE.phoneHref} data-testid="fans-upgrade-call">
                                <Button className="rounded-none h-12 px-6 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold font-['Manrope']">
                                    <Phone className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                    CALL {SITE.phoneDisplay}
                                </Button>
                            </a>
                            <a href="/#contact" data-testid="fans-upgrade-form">
                                <Button variant="outline" className="rounded-none h-12 px-6 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold font-['Manrope']">
                                    GET A QUOTE
                                </Button>
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase text-[#111111] mb-5">Signs it&apos;s time for a fan upgrade</div>
                        <ul className="grid sm:grid-cols-2 gap-3">
                            {UPGRADE_REASONS.map((r) => (
                                <li key={r} className="bg-[#F6F5F2] border border-[#E2DFD8] px-4 py-3 flex items-start gap-2 text-sm font-semibold text-[#111812]">
                                    <Wrench className="w-4 h-4 text-[#E11D2A] shrink-0 mt-0.5" strokeWidth={2.5} />
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8 grid sm:grid-cols-3 gap-3">
                            <div className="bg-[#111111] text-[#F6F5F2] p-5">
                                <Home className="w-5 h-5 text-[#FFD400] mb-3" strokeWidth={2.25} />
                                <div className="font-display text-base font-extrabold">Whole-house</div>
                                <div className="font-['Manrope'] text-xs text-[#F6F5F2]/70 mt-1">Motor swaps, insulated dampers, quieter ducted models.</div>
                            </div>
                            <div className="bg-[#111111] text-[#F6F5F2] p-5">
                                <Zap className="w-5 h-5 text-[#FFD400] mb-3" strokeWidth={2.25} />
                                <div className="font-display text-base font-extrabold">Attic</div>
                                <div className="font-['Manrope'] text-xs text-[#F6F5F2]/70 mt-1">Solar-powered conversions, smart thermostats, balanced intake.</div>
                            </div>
                            <div className="bg-[#111111] text-[#F6F5F2] p-5">
                                <Droplets className="w-5 h-5 text-[#FFD400] mb-3" strokeWidth={2.25} />
                                <div className="font-display text-base font-extrabold">Bath</div>
                                <div className="font-['Manrope'] text-xs text-[#F6F5F2]/70 mt-1">Light/heat/fan combos, humidity-sensing, properly ducted to outside.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#FFD400] border-b-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111111] text-4xl sm:text-5xl">
                            Cooler house. Drier bathroom. Smaller bill.
                        </h2>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#111111]/85 max-w-2xl">
                            Free quotes. Honest sizing. Code-compliant install. Real Oregon contractor (CCB #232477) with 25 years on the tools.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3">
                        <a href={SITE.phoneHref} className="block flex-1" data-testid="fans-cta-call">
                            <Button className="rounded-none w-full h-14 px-6 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" className="block flex-1" data-testid="fans-cta-form">
                            <Button variant="outline" className="rounded-none w-full h-14 px-6 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold tracking-wide text-base font-['Manrope']">
                                REQUEST A QUOTE
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Image attribution */}
            <section className="bg-[#111111] text-[#F6F5F2]/55 border-t border-[#F6F5F2]/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-5 text-[11px] font-['Manrope'] leading-relaxed">
                    Example whole-house, attic, and bathroom fan photos via Wikimedia Commons (CC BY-SA). Used for illustration of equipment types; not photos of jobs we&apos;ve personally completed.
                </div>
            </section>

            <Footer />
        </div>
    );
}
